import React, { useEffect, useState } from "react";
import { Card, TextArea, FormGroup } from "@blueprintjs/core";
import gipfelglueckImg from "./assets/Framing_1_Gipfelglueck.png";
import wipfelreichImg from "./assets/Framing_1_Wipfelreich.png";
import { ScreenProps } from "../../SequenceController";
import { ScreenConfig } from "../Sequences";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles } from "../../../../components/colors";

type ScreenClick = {
    choice: number;
    millisecondsSinceFirstRender: number;
    reasoning?: string;
}

export type Screen2Result = {
    selections: Array<ScreenClick>,
    usedHelp: boolean
    explanation: string;
}

export const FramingScreen2Result: Screen2Result = {
    selections: [],
    explanation: "",
    usedHelp: false
}

export const FramingScreen2 = (props: ScreenProps) => {
    const { t, language } = useLanguage();
    const [firstRenderTimestamp, setFirstRenderTimestamp] = useState(0);
    const [explanation, setExplanation] = useState("");
    const { setCanContinue, result, setResult } = props;

    useEffect(() => {
        setFirstRenderTimestamp(Date.now());
    }, [setResult]);

    // Defensive: always treat selections as an array
    const selections = Array.isArray(result?.selections) ? result.selections : [];
    const selected = selections.length > 0 ? selections[selections.length - 1].choice : -1;

    useEffect(() => {
        setCanContinue(selected !== -1 && explanation.trim().length > 0);
    }, [explanation, selected, setCanContinue]);

    // FIX: removed setCanContinue(true) here!
    const addSelection = (choice: number) => {
        setResult((prev: Screen2Result) => {
            const newSelections = Array.isArray(prev.selections) ? [...prev.selections] : [];
            newSelections.push({ choice, millisecondsSinceFirstRender: Date.now() - firstRenderTimestamp });
            return { ...prev, selections: newSelections };
        });
    }

    const onExplanationChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setExplanation(event.target.value);
        setResult((prev: Screen2Result) => ({
            ...prev,
            explanation: event.target.value
        }));
    }

    const title = t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'title') ||
        (language === 'en' ? "Climbing Park Dilemma" : "Die Entscheidung im Kletterpark-Dilemma");

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <div style={{ ...commonStyles.card, maxWidth: 880, margin: "0 auto", padding: 36 }}>
                <h2 style={{
                    color: colors.brillantBlue,
                    margin: "0 0 26px 0",
                    fontSize: 28,
                    fontWeight: 700,
                    textAlign: "center",
                    letterSpacing: 0.5
                }}>
                    {title}
                </h2>
                <div style={{
                    ...commonStyles.paragraph,
                    fontSize: 17,
                    color: colors.textDark,
                    margin: "0 0 18px 0",
                    textAlign: "justify"
                }}>
                    <p>{t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'content1') || "Die vier achten Klassen (insgesamt 100 Kinder) wollen einen  gemeinsamen Ausflug in den Kletterpark unternehmen. In der Nähe der Schule gibt es zwei tolle Möglichkeiten für den Ausflug: den Kletterpark Wipfelreich und Kletterpark Gipfelglück. Allerdings gibt es ein Problem: In der Woche des Ausflugs haben auch andere Schulen ihren Wandertag. Es könnte daher sein, dass in den Parks nicht genug Kletterausrüstungen für alle Schülerinnen und Schüler zur Verfügung stehen werden."}</p>
                    <p>{t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'content2') || "Die Schule muss sich zwischen den zwei Optionen entscheiden:"}</p>
                </div>

                <div className="doublecard-container" style={{
                    display: 'flex',
                    gap: '28px',
                    justifyContent: 'center',
                    margin: '32px 0'
                }}>
                    <Card
                        className="doublecard"
                        interactive={true}
                        style={{
                            ...commonStyles.optionCard.default,
                            maxWidth: 370,
                            border: selected === 1
                                ? `2px solid ${colors.lichtBlau}`
                                : `1px solid ${colors.silverLight}`,
                            backgroundColor: selected === 1
                                ? colors.lichtBlauVeryLight
                                : colors.backgroundLight,
                            boxShadow: selected === 1
                                ? `0 0 14px ${colors.lichtBlauLighter}`
                                : "0 2px 10px rgba(0,0,0,0.08)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center"
                        }}
                        onClick={() => { addSelection(1) }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            marginBottom: 18,
                        }}>
                            <img
                                src={wipfelreichImg}
                                alt={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'option1Title') || "Plan A: Wipfelreich"}
                                style={{
                                    width: "100%",
                                    maxWidth: 290,
                                    height: 210,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
                                    display: "block"
                                }}
                            />
                        </div>
                        <h4 style={{
                            color: colors.brillantBlue,
                            fontSize: 20,
                            fontWeight: 600,
                            margin: "0 0 10px 0",
                            textAlign: "center"
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'option1Title') || "Option 1: Wipfelreich"}
                        </h4>
                        <p style={{
                            color: colors.textDark,
                            fontSize: 17,
                            textAlign: "justify",
                            margin: 0,
                            lineHeight: 1.7
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'option1Desc') || "Die Klassen buchen eine Sonderveranstaltung im Kletterpark Wipfelreich. Der Betreiber der Anlage hat zugesagt, dass er Kletterausrüstung für genau 75 Kinder hat, die dort sicher teilnehmen können."}
                        </p>
                    </Card>
                    <Card
                        className="doublecard"
                        interactive={true}
                        style={{
                            ...commonStyles.optionCard.default,
                            maxWidth: 370,
                            border: selected === 2
                                ? `2px solid ${colors.lichtBlau}`
                                : `1px solid ${colors.silverLight}`,
                            backgroundColor: selected === 2
                                ? colors.lichtBlauVeryLight
                                : colors.backgroundLight,
                            boxShadow: selected === 2
                                ? `0 0 14px ${colors.lichtBlauLighter}`
                                : "0 2px 10px rgba(0,0,0,0.08)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center"
                        }}
                        onClick={() => { addSelection(2) }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            marginBottom: 18,
                        }}>
                            <img
                                src={gipfelglueckImg}
                                alt={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'option2Title') || "Plan B: Gipfelglück"}
                                style={{
                                    width: "100%",
                                    maxWidth: 290,
                                    height: 210,
                                    objectFit: "cover",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
                                    display: "block"
                                }}
                            />
                        </div>
                        <h4 style={{
                            color: colors.brillantBlue,
                            fontSize: 20,
                            fontWeight: 600,
                            margin: "0 0 10px 0",
                            textAlign: "center"
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'option2Title') || "Option 2: Gipfelglück"}
                        </h4>
                        <p style={{
                            color: colors.textDark,
                            fontSize: 17,
                            textAlign: "justify",
                            margin: 0,
                            lineHeight: 1.7
                        }}>
                            {t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'option2Desc') || "Die Klassen machen einen regulären Besuch im Kletterpark Gipfelglück, in dem man nicht reservieren kann. Dort gibt es eine 75%-Chance, dass alle 100 Kinder teilnehmen können – aber auch eine 25%-Chance, dass keines der Kinder teilnehmen kann, wenn keine Kletterausrüstungen mehr verfügbar sind."}
                        </p>
                    </Card>
                </div>

                <div style={{
                    fontSize: 17,
                    color: colors.textDark,
                    marginBottom: 10,
                    textAlign: "justify",
                    lineHeight: 1.7
                }}>
                    <p>{t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'choosePrompt') || "Wähle die Option aus, die die Schulleitung deiner Meinung nach wählen sollte!"}</p>
                </div>

                <div className="reasoning-section" style={{ marginTop: 30 }}>
                    <FormGroup
                        label={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'reasonLabel') || "Begründe deine Entscheidung!"}
                        labelFor="reasoning-input"
                        labelInfo={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'reasonRequired') || "(erforderlich)"}
                    >
                        <TextArea
                            id="reasoning-input"
                            placeholder={t('experiments', 'RiskyChoiceFraming', 'screens', 'screen2', 'reasonPlaceholder') || "Schreibe hier deine Begründung..."}
                            value={explanation}
                            onChange={onExplanationChange}
                            fill={true}
                            autoResize={true}
                        />
                    </FormGroup>
                </div>
            </div>
        </div>
    );
};

export const FramingScreen2Config: ScreenConfig = {
    component: FramingScreen2,
    defaultResult: FramingScreen2Result,
    canContinueDefault: true
}